// Run async tasks with bounded concurrency. `tasks` is an array of thunks
// (each a `() => Promise`); at most `limit` run at once and results preserve
// input order. Task rejections propagate, so callers wanting best-effort
// behaviour should `.catch()` inside their thunk (matching the existing
// all()-based preload usage this replaces).
//
// Why: the reporting preloads fan out one query per (subject × student), which
// on a large group fires hundreds of requests in a single tick and overwhelms
// the browser's connection pool. Draining them through a small pool keeps the
// number of in-flight requests bounded.
export default async function mapWithConcurrency<T>(
  tasks: readonly (() => Promise<T>)[],
  limit: number,
): Promise<T[]> {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new Error('mapWithConcurrency: limit must be a positive integer');
  }
  const results = new Array<T>(tasks.length);
  let next = 0;
  const workerCount = Math.max(1, Math.min(limit, tasks.length));
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      // i < tasks.length, checked above.
      results[i] = await tasks[i]!();
    }
  };
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}
